import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Check,
  X,
  ChevronRight,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Select } from '@/components/atoms/Select';
import { Badge } from '@/components/atoms/Badge';
import { Card } from '@/components/molecules/Card';
import * as conversationsApi from '@/services/api/conversations.api';
import { useGenerateConversation } from '@/hooks/useGenerateConversation';
import { ApiError } from '@/services/api/client';
import { IntegrityRetryModal } from '@/components/molecules/IntegrityRetryModal';
import { buildRoute, ROUTES } from '@/constants/routes';
import type { EnglishLevel } from '@/types/story.types';
import type {
  ConversationSituationDocument,
  ConversationPracticeFormData,
} from '@/types/conversation.types';
import styles from '../GenerateStoryPage/GenerateStoryPage.module.scss';

const LEVELS = [
  { label: 'A1 — Beginner', value: 'A1' },
  { label: 'A2 — Elementary', value: 'A2' },
  { label: 'B1 — Intermediate', value: 'B1' },
  { label: 'B2 — Upper-Intermediate', value: 'B2' },
  { label: 'C1 — Advanced', value: 'C1' },
  { label: 'C2 — Mastery', value: 'C2' },
];

function displayTitle(theme: string, doc: ConversationSituationDocument): string {
  const t = doc.situation.title?.trim();
  if (t) return t;
  const short = theme.trim().slice(0, 72);
  return short ? `Conversación · ${short}` : 'Conversación generada';
}

function buildConversationPracticeDraft(
  theme: string,
  doc: ConversationSituationDocument,
  level: EnglishLevel
): ConversationPracticeFormData {
  const description =
    doc.situation.objective?.trim() ||
    (doc.situation.context?.trim() ?? '').slice(0, 280) ||
    theme.trim().slice(0, 280) ||
    'Conversación generada por IA';
  return {
    title: displayTitle(theme, doc),
    description,
    level,
    status: 'draft',
    estimatedTime: 25,
    xpReward: 100,
  };
}

export function GenerateConversationPage() {
  const navigate = useNavigate();

  const [topic, setTopic] = useState('');
  const [level, setLevel] = useState<EnglishLevel>('B1');

  const {
    status,
    progressMessages,
    generatedDocument,
    error,
    integrityErrors,
    auditingDocument,
    generate,
    retryIntegrityWithAI,
    setIntegrityErrorsFromSave,
    clearIntegrityErrors,
    reset,
  } = useGenerateConversation();

  const [savingPhase, setSavingPhase] = useState<'idle' | 'saving' | 'image'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedResourceId, setSavedResourceId] = useState<string | null>(null);
  /** Aviso al backoffice si quedaron líneas NPC sin audio (puede reintentar en el panel de detalle). */
  const [audioWarning, setAudioWarning] = useState<string | null>(null);
  const [integrityModalOpen, setIntegrityModalOpen] = useState(false);
  const isSavingFlow = savingPhase !== 'idle';

  // Sincroniza la apertura del modal con la presencia de errores: se abre
  // cuando llegan errores y se cierra solo cuando un retry exitoso los limpia.
  useEffect(() => {
    setIntegrityModalOpen(integrityErrors.length > 0);
  }, [integrityErrors]);

  const handleCloseIntegrityModal = (open: boolean) => {
    setIntegrityModalOpen(open);
    if (!open) clearIntegrityErrors();
  };

  const generateResourceImage = async (resourceId: string) => {
    setSavingPhase('image');
    setSaveError(null);
    try {
      await conversationsApi.generateConversationPracticeBackgroundImage(resourceId);
      navigate(buildRoute(ROUTES.BACKOFFICE.CONVERSATION_EDIT, { id: resourceId }));
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Error desconocido';
      setSaveError(
        `Recurso y conversación guardados, pero la generación de imagen falló: ${detail}`
      );
      setSavingPhase('idle');
    }
  };

  const handleRetryImage = () => {
    if (savedResourceId) void generateResourceImage(savedResourceId);
  };

  const handleSkipImage = () => {
    if (savedResourceId) {
      navigate(buildRoute(ROUTES.BACKOFFICE.CONVERSATION_EDIT, { id: savedResourceId }));
    }
  };

  const runGenerate = () => {
    if (!topic.trim()) return;
    setSaveError(null);
    setSavedResourceId(null);
    setSavingPhase('idle');
    setAudioWarning(null);
    void generate({ level, context: topic.trim(), studentRole: null });
  };

  const handleReject = () => {
    reset();
    setSaveError(null);
    setSavingPhase('idle');
    setSavedResourceId(null);
    setAudioWarning(null);
  };

  const handleApproveAndSave = async () => {
    if (!generatedDocument) return;
    setSaveError(null);
    setAudioWarning(null);
    setSavingPhase('saving');
    try {
      const form = buildConversationPracticeDraft(topic, generatedDocument, level);
      const saved = await conversationsApi.saveConversationPractice(null, form);

      const persisted = await conversationsApi.putConversationPracticeSituation(saved.id, {
        document: generatedDocument,
        contentItemId: null,
      });

      const missing = persisted.missingAudios ?? [];
      if (persisted.audioStats && missing.length > 0) {
        setAudioWarning(
          `Se generaron ${persisted.audioStats.processedLines}/${persisted.audioStats.totalLines} audios NPC. Quedan ${missing.length} pendientes — puedes reintentar desde la ficha del recurso.`
        );
      }

      setSavedResourceId(saved.id);
      await generateResourceImage(saved.id);
    } catch (e) {
      // Si el endpoint rechaza por validación, mostramos el modal con los
      // detalles para que el editor pueda pedir un retry con IA.
      if (
        e instanceof ApiError &&
        e.status === 400 &&
        e.data &&
        typeof e.data === 'object'
      ) {
        const raw = (e.data as { details?: unknown }).details;
        const details = Array.isArray(raw) ? raw.filter((s): s is string => typeof s === 'string') : null;
        if (details && details.length > 0) {
          setIntegrityErrorsFromSave(details);
          setSavingPhase('idle');
          return;
        }
      }
      setSaveError(e instanceof Error ? e.message : 'Error al guardar la conversación');
      setSavingPhase('idle');
    }
  };

  if (status === 'generating') {
    return (
      <div className={styles.page}>
        <div className={styles.page__header}>
          <Sparkles size={24} className={styles.page__icon} />
          <div>
            <h1>Generando conversación…</h1>
            <p className={styles.page__subtitle}>
              Puede tardar 30–120 segundos. La IA prepara el diálogo y validamos la estructura.
            </p>
          </div>
        </div>

        <Card className={styles.progress}>
          <div className={styles.progress__spinner} aria-hidden="true" />
          {progressMessages.length > 0 && (
            <ul className={styles.progress__messages}>
              {progressMessages.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    );
  }

  if (status === 'complete' && generatedDocument) {
    const s = generatedDocument.situation;

    return (
      <div className={styles.page}>
        <IntegrityRetryModal
          open={integrityModalOpen}
          onOpenChange={handleCloseIntegrityModal}
          errors={integrityErrors}
          onRetry={() => void retryIntegrityWithAI()}
          retrying={auditingDocument}
        />
        <div className={styles.page__header}>
          <Check size={24} className={styles.page__iconSuccess} />
          <div>
            <h1>Vista previa</h1>
            <p className={styles.page__subtitle}>
              Revisa el contenido generado. Al aprobar se creará el recurso como borrador, se guardará la
              conversación y luego se generará la imagen de fondo (igual que en las historias), y se abrirá la ficha
              del recurso.
            </p>
          </div>
        </div>

        <Card className={styles.preview__meta}>
          <h2 className={styles.preview__title}>{displayTitle(topic, generatedDocument)}</h2>
          <p className={styles.preview__description}>{s.objective || s.context}</p>
          <div className={styles.preview__badges}>
            <Badge variant="info">{level}</Badge>
            <Badge variant="default">{s.interlocutor.name}</Badge>
            <Badge variant="default">{s.interlocutor.role}</Badge>
            <Badge variant="default">
              {s.turns.filter((t) => t.speaker === 'student').length} decisiones
            </Badge>
          </div>
        </Card>

        <Card className={styles.preview__section}>
          <h3 className={styles.preview__sectionTitle}>Contexto (IA)</h3>
          <p className={styles.preview__text}>{s.context}</p>
        </Card>

        <Card className={styles.preview__section}>
          <h3 className={styles.preview__sectionTitle}>Rol del estudiante</h3>
          <p className={styles.preview__text}>{s.student_role}</p>
        </Card>

        <Card className={styles.preview__section}>
          <details>
            <summary className={styles.preview__sectionTitle}>JSON completo</summary>
            <pre className={styles.preview__pre}>{JSON.stringify(generatedDocument, null, 2)}</pre>
          </details>
        </Card>

        {saveError && (
          <div className={styles.errorBox}>
            <AlertCircle size={16} />
            <span>{saveError}</span>
          </div>
        )}
        {audioWarning && (
          <div className={styles.errorBox}>
            <AlertCircle size={16} />
            <span>{audioWarning}</span>
          </div>
        )}
        <div className={styles.preview__actions}>
          {savedResourceId && saveError ? (
            <>
              <Button variant="ghost" onClick={handleSkipImage}>
                Continuar sin imagen
              </Button>
              <Button leftIcon={<RefreshCw size={16} />} onClick={handleRetryImage}>
                Reintentar imagen
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                leftIcon={<X size={16} />}
                onClick={handleReject}
                disabled={isSavingFlow}
              >
                Volver y editar tema
              </Button>
              <Button
                variant="ghost"
                leftIcon={<RefreshCw size={16} />}
                onClick={runGenerate}
                disabled={isSavingFlow}
              >
                Regenerar
              </Button>
              <Button
                leftIcon={<Check size={16} />}
                onClick={() => void handleApproveAndSave()}
                disabled={isSavingFlow}
              >
                {savingPhase === 'saving'
                  ? 'Guardando recurso y conversación…'
                  : savingPhase === 'image'
                    ? 'Generando imagen…'
                    : 'Aprobar y guardar'}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.page__header}>
        <Sparkles size={24} className={styles.page__icon} />
        <div>
          <h1>Generar conversación con IA</h1>
          <p className={styles.page__subtitle}>
            Describe la situación o tema y el nivel CEFR. La IA creará el diálogo interactivo. Al aprobar, se creará el
            recurso como borrador y, en serie, la imagen de fondo (mismo flujo que al generar una historia desde cero).
          </p>
        </div>
      </div>

      <Card className={styles.form}>
        <div className={styles.form__field}>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className={styles.form__label}>Situación o tema</label>
          <textarea
            className={styles.form__textarea}
            rows={4}
            placeholder="Ej.: check‑in temprano en un hotel tras un vuelo transatlántico; el recepcionista es cordial pero el sistema ha perdido tu reserva…"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
          />
        </div>

        <div className={styles.form__field}>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className={styles.form__label}>Nivel CEFR</label>
          <Select
            value={level}
            onValueChange={(v) => setLevel(v as EnglishLevel)}
            options={LEVELS}
            placeholder="Seleccionar nivel"
          />
        </div>

        {error && (
          <div className={styles.errorBox}>
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <div className={styles.form__actions}>
          <Button variant="ghost" onClick={() => navigate(ROUTES.BACKOFFICE.CONVERSATIONS)}>
            Cancelar
          </Button>
          <Button onClick={runGenerate} disabled={!topic.trim()} rightIcon={<ChevronRight size={16} />}>
            Generar
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default GenerateConversationPage;
